import { type Location, Role } from '@piccolohealth/pbs-common';
import { ChakraV3, Spin } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FaUser, FaUsers, FaUsersCog } from 'react-icons/fa';
import { FormStack } from '../../components/forms/FormStack';
import { HookedAvatarUploader } from '../../components/forms/HookedAvatarUploader';
import { HookedCheckboxStack } from '../../components/forms/HookedCheckboxStack';
import { HookedFeatureFlagSwitches } from '../../components/forms/HookedFeatureFlagSwitches';
import { HookedFormField } from '../../components/forms/HookedFormField';
import { HookedFormFieldset } from '../../components/forms/HookedFormFieldset';
import { HookedInput } from '../../components/forms/HookedInput';
import { HookedMultiSelect } from '../../components/forms/HookedMultiSelect';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { useOrganizationQuery } from '../../graphql/hooks/useOrganizationQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { useRole } from '../../hooks/useRole';

export const UserForm = () => {
  const { organization } = useAppContext();

  const roles = useWatch({ name: 'roles' });

  const { setValue } = useFormContext();

  const { isLoading, data, error } = useOrganizationQuery({
    organizationId: organization.id,
  });

  const hasSupportRole = useRole(Role.Support);

  const canSelectLocations = React.useMemo(() => {
    return roles.includes(Role.TeamLeader) || roles.includes(Role.TeamMember);
  }, [roles]);

  const locationOptions = React.useMemo(() => {
    const locations = (data?.organization?.locations as Location[]) ?? [];

    const options = locations.map((location) => ({
      label: location.name,
      value: location.id,
      raw: location,
      color: location.color,
    }));

    return P.orderBy(options, (o) => o.label);
  }, [data?.organization?.locations]);

  React.useEffect(() => {
    if (!canSelectLocations) {
      setValue('locations', []);
    }
  }, [canSelectLocations, setValue]);

  if (isLoading) {
    return <Spin />;
  }

  if (error) {
    return <PiccoloError error={error} />;
  }

  return (
    <FormStack>
      <FormStack orientation='horizontal' gap={8}>
        <FormStack gap={5}>
          <HookedFormField name='name' label='Name' required>
            <HookedInput name='name' size='sm' placeholder='Enter name' />
          </HookedFormField>
          <HookedFormField name='email' label='Email' required>
            <HookedInput name='email' size='sm' placeholder='Enter email' />
          </HookedFormField>
          <HookedFormField name='title' label='Title' required>
            <HookedInput name='title' size='sm' placeholder='Enter title' />
          </HookedFormField>
          <HookedFormField name='practitionerNumber' label='Practitioner number'>
            <HookedInput
              name='practitionerNumber'
              size='sm'
              placeholder='Enter practitioner number'
            />
          </HookedFormField>
        </FormStack>

        <ChakraV3.Stack pt={7}>
          <HookedAvatarUploader name='picture' size='xl' />
        </ChakraV3.Stack>
      </FormStack>

      <ChakraV3.Separator />

      <HookedFormFieldset name='roles' label='Roles'>
        <HookedCheckboxStack
          name='roles'
          options={[
            {
              label: 'Administrator',
              value: Role.Administrator,
              description:
                'Can manage all aspects of the organization. Has access to all participants',
              icon: (
                <ChakraV3.Icon>
                  <FaUsersCog />
                </ChakraV3.Icon>
              ),
            },
            {
              label: 'Team leader',
              value: Role.TeamLeader,
              description:
                'Can manage all aspects of their team. Has access to only the participants they are assigned',
              icon: (
                <ChakraV3.Icon>
                  <FaUsers />
                </ChakraV3.Icon>
              ),
            },
            {
              label: 'Team member',
              value: Role.TeamMember,
              description:
                'Has access to only the participants they are assigned. Cannot manage participants',
              icon: (
                <ChakraV3.Icon>
                  <FaUser />
                </ChakraV3.Icon>
              ),
            },
          ]}
        />
      </HookedFormFieldset>

      <ChakraV3.Separator />

      {canSelectLocations && (
        <HookedFormField name='locations' label='Locations'>
          <HookedMultiSelect
            placeholder='Select locations'
            name='locations'
            options={locationOptions}
            optionVariant='tag'
          />
        </HookedFormField>
      )}

      {hasSupportRole && (
        <HookedFormFieldset name='featureFlags' label='Feature flags'>
          <HookedFeatureFlagSwitches name='featureFlags' />
        </HookedFormFieldset>
      )}
    </FormStack>
  );
};
